import React from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  AlertTriangle,
  Trash2,
  Loader2,
  User,
  Mail,
  Shield,
} from "lucide-react";

const DeleteAdminModal = ({
  isOpen,
  adminToDelete,
  onClose,
  onConfirm,
  loading,
}) => {
  if (!isOpen || !adminToDelete) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="overflow-hidden relative w-full max-w-md bg-white rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex justify-center items-center p-6 bg-gradient-to-br from-red-50 to-orange-50">
            <div className="flex flex-col gap-3 items-center text-center">
              <div className="flex justify-center items-center p-3 w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-full shadow-lg">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Delete Administrator
                </h3>
                <p className="mt-1 text-xs text-gray-600">
                  This action cannot be undone
                </p>
              </div>
            </div>
          </div>

          {/* Modal Body */}
          <div className="p-4">
            <div className="text-center">
              <p className="mb-3 text-sm text-gray-700">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-red-600">
                  {adminToDelete.first_name} {adminToDelete.last_name}
                </span>
                ?
              </p>

              {/* Admin Details Card */}
              <div className="p-3 mb-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex gap-2 items-start">
                  <div className="flex-shrink-0 p-1.5 bg-blue-100 rounded-md">
                    <User className="w-3 h-3 text-blue-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="mb-1 text-sm font-medium text-gray-900">
                      {adminToDelete.first_name} {adminToDelete.last_name}
                    </h4>
                    <div className="space-y-0.5 text-xs text-gray-600">
                      <div className="flex gap-1 items-center">
                        <Mail className="w-3 h-3" />
                        <span className="truncate">{adminToDelete.email}</span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <Shield className="w-3 h-3" />
                        <span>{adminToDelete.role || "Administrator"}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 items-center mt-1">
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                          adminToDelete.status === 1 ||
                          adminToDelete.status === "1" ||
                          adminToDelete.status === "active"
                            ? "text-red-700 bg-red-100"
                            : "text-orange-700 bg-orange-100"
                        }`}
                      >
                        {adminToDelete.status === 1 ||
                        adminToDelete.status === "1" ||
                        adminToDelete.status === "active"
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Warning Section */}
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex gap-2 items-start">
                  <div className="flex-shrink-0 p-1 bg-red-100 rounded-md">
                    <AlertTriangle className="w-3 h-3 text-red-600" />
                  </div>
                  <div className="text-left">
                    <p className="mb-1 text-xs font-medium text-red-800">
                      Warning
                    </p>
                    <div className="space-y-0.5 text-xs text-red-600">
                      <p>• This will permanently remove the administrator</p>
                      <p>• All permissions will be revoked</p>
                      <p>• This action cannot be reversed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex gap-2 p-4 bg-gray-50 border-t border-gray-200">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onConfirm}
              disabled={loading}
              className="flex flex-1 gap-1.5 justify-center items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-orange-500 rounded-lg shadow-lg transition-all duration-200 hover:from-red-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DeleteAdminModal;
